// Map page entry. Leaflet-based interactive map of all bookable points
// across the four backend kinds (PowerStation, AC ChargingStation,
// DC ChargingStation, WaterPoint). Fetches PowerStations + WaterPoints once
// (small datasets) and AC + DC by viewport bounds. Status colours apply
// when the point has bookings - currently only PowerStations.
//
// Polygon-drawing area-request tool + book-modal land in follow-up commits.
import './site';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { envConfig, loadConfig } from '../envConfig';
import { api } from './api';
import { addBaseLayer } from './basemap';
import { getLanguage, t } from './i18n/i18n';
// Eager import of the booking modal so its picking-mode state is readable
// synchronously during detail-card render (we used to lazy-import it on
// first click; can't, because the card's primary-CTA label depends on
// whether the modal is currently in "add another station" mode).
import { openBookModal, addPickedPoint, isPickingMore } from './bookModal';
import { hasStoredSession, usageHref } from './lib/pointUsage';

interface MapPoint {
  /** PointKind discriminator matching backend Models/PointKinds. */
  kind: 'PowerStation' | 'IndirectStation' | 'AcChargingStation' | 'DcChargingStation' | 'WaterPoint';
  id: number;
  name: string;
  address: string;
  city?: string | null;
  lat: number;
  lng: number;
  status: 'available' | 'maintenance' | 'out_of_service';
  /** Backend-provided tariff string, e.g. "0.32 €/kWh"; only on power stations. */
  tariff?: string | null;
  /** Operator/organisation label. */
  operator?: string | null;
  /** Short human label for the kind (e.g. "AC laadpaal", "Bouwaansluiting"). Shown in the result list meta line. */
  typeLabel?: string;
  /** Spec pairs for the result-list pills (value-only) and the detail panel grid (label + value). */
  details?: Array<{ labelKey: string; value: string }>;
  /** Public image URLs in display order; currently populated for power stations only. */
  images?: string[];
  /** The point's own connector types - scopes the request connector dropdown. */
  connectorTypes?: string[];
}

const layerVisible: Record<string, boolean> = {
  PowerStation: true,
  IndirectStation: true,
  AcChargingStation: true,
  DcChargingStation: true,
  WaterPoint: true,
};
const statusVisible: Record<string, boolean> = {
  available: true,
  maintenance: true,
  out_of_service: true,
};

function isPointStatus(value: unknown): value is MapPoint['status'] {
  return value === 'available' || value === 'maintenance' || value === 'out_of_service';
}
function pointStatus(value: unknown): MapPoint['status'] {
  return isPointStatus(value) ? value : 'available';
}
const points: MapPoint[] = [];
const markersById: Record<string, L.Marker> = {};

// Charging-station clusters: the dataset is ~200k stations - far too many to
// ship as individual markers (payload size + scraping exposure), so at zoom
// <= CLUSTER_ZOOM_MAX we show server-computed per-city counts and only load
// individual stations from /by-bounds once zoomed past it. The per-city
// markers are fed through their own Leaflet markercluster group (below), so
// nearby cities merge into a single bubble instead of leaving one static dot
// per city at country zoom - the same grouping behaviour as the normal points.
//
// Kept at 14 so individual stations appear from zoom 15 (neighbourhood level):
// the city cluster bubble is anchored at the city centre, so at higher cluster
// thresholds a user zoomed onto a suburb saw neither the (off-screen) bubble
// nor any stations until ~zoom 17. A zoom-15 viewport (~0.02–0.06° tall) stays
// well within the backend's by-bounds size cap, so the switch is safe.
const CLUSTER_ZOOM_MAX = 14;
interface ClusterRow { region: string; count: number; lat: number; lng: number; }
const rawClusters: { ac: ClusterRow[]; dc: ClusterRow[] } = { ac: [], dc: [] };
/** Marker option carrying a city's station count so the cluster icon can sum its children. */
interface ChargerClusterMarkerOptions extends L.MarkerOptions { stationCount?: number; }

function isClusterZoom(): boolean {
  return map.getZoom() <= CLUSTER_ZOOM_MAX;
}

await loadConfig();

export const map = L.map('map', { zoomControl: true, scrollWheelZoom: true })
  .setView([envConfig.brand.mapCenter[1], envConfig.brand.mapCenter[0]], envConfig.brand.mapZoom);

addBaseLayer(map);

// Single marker-cluster group for all four kinds' individual markers. At any
// zoom, dense areas auto-aggregate into a numbered cluster bubble. The
// server-side per-city AC/DC clusters live in their own group
// (chargerClusterGroup, below) - only used at country/region zoom where the
// individual AC/DC stations aren't loaded.
const pointClusterGroup = L.markerClusterGroup({
  chunkedLoading: true,
  showCoverageOnHover: false,
  spiderfyOnMaxZoom: true,
  maxClusterRadius: 60,
});
pointClusterGroup.addTo(map);

// City-level charging-station clusters get their OWN markercluster group so
// that - like the individual-point clusters above - nearby city bubbles merge
// into one bubble (showing the combined station count) and split apart as you
// zoom in, instead of dozens of static per-city dots at country zoom. Added
// to / removed from the map by applyVisibility based on the zoom threshold.
const chargerClusterGroup = L.markerClusterGroup({
  chunkedLoading: true,
  showCoverageOnHover: false,
  zoomToBoundsOnClick: true,
  maxClusterRadius: 60,
  iconCreateFunction: (cluster) => {
    const total = cluster.getAllChildMarkers()
      .reduce((sum, m) => sum + ((m.options as ChargerClusterMarkerOptions).stationCount ?? 0), 0);
    return defaultClusterIcon(total);
  },
});

/** Compact count for a cluster bubble - 1234 → "1.2k" - so big totals stay inside the dot. */
function formatClusterCount(n: number): string {
  return n >= 1000 ? `${Math.round(n / 100) / 10}k` : String(n);
}

/**
 * Builds a divIcon byte-for-byte identical to Leaflet.markercluster's default
 * bubble (the `marker-cluster` + size-class markup with `<div><span>count</span></div>`),
 * so the per-city charger bubbles are indistinguishable from the normal-point
 * clusters. Size bucket follows the plugin's own <10 / <100 thresholds.
 */
function defaultClusterIcon(count: number): L.DivIcon {
  const size = count < 10 ? 'small' : count < 100 ? 'medium' : 'large';
  return L.divIcon({
    html: `<div><span>${formatClusterCount(count)}</span></div>`,
    className: `marker-cluster marker-cluster-${size}`,
    iconSize: L.point(40, 40),
  });
}

// ---- helpers ----------------------------------------------------------------

function pointKey(p: { kind: string; id: number }): string { return `${p.kind}-${p.id}`; }

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Marker colour is driven by the point's kind (PowerStation, IndirectStation,
// AcChargingStation, DcChargingStation, WaterPoint) - see the matching CSS
// `.sk-marker--<class>` rules + the legend on map.html.
const KIND_TO_MARKER_CLASS: Record<MapPoint['kind'], string> = {
  PowerStation: 'power-station',
  IndirectStation: 'indirect-station',
  AcChargingStation: 'ac-charger',
  DcChargingStation: 'dc-charger',
  WaterPoint: 'water-point',
};

function makeIcon(p: MapPoint): L.DivIcon {
  const kindClass = KIND_TO_MARKER_CLASS[p.kind];
  return L.divIcon({
    className: '',
    html: `<div class="sk-marker sk-marker--${kindClass}"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/></svg></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

// Render order when markers overlap. Power stations are the editorial focus
// of this site, so they always sit on top of any nearby water point or
// charging station - independent of which layer loaded first or what
// Leaflet's default lat-based sort would have picked.
const MARKER_Z_INDEX: Record<MapPoint['kind'], number> = {
  PowerStation: 2000,
  IndirectStation: 2000,
  WaterPoint: 1000,
  AcChargingStation: 0,
  DcChargingStation: 0,
};

// Sort order for the #map-results list. Mirrors the editorial intent of the
// marker z-index above - power stations head the list, then water points,
// then AC/DC charging stations. Without this the list reads in
// fetch-completion order, which lands the (small) water-points layer above
// the (slower) power-stations layer.
const RESULT_KIND_ORDER: Record<MapPoint['kind'], number> = {
  PowerStation: 0,
  IndirectStation: 1,
  WaterPoint: 2,
  AcChargingStation: 3,
  DcChargingStation: 4,
};

function refreshMarker(p: MapPoint): void {
  const key = pointKey(p);
  const existing = markersById[key];
  if (existing) {
    existing.setIcon(makeIcon(p));
    return;
  }
  const marker = L.marker([p.lat, p.lng], {
    icon: makeIcon(p),
    zIndexOffset: MARKER_Z_INDEX[p.kind],
  });
  marker.on('click', () => selectPoint(p, false));
  markersById[key] = marker;
  if (isVisible(p)) pointClusterGroup.addLayer(marker);
}

let activePointKey: string | null = null;

/**
 * Opens the right-hand detail panel for a point and (optionally) flies the
 * map to it. Replaces the per-marker popup. Closes via the panel's close
 * button or by selecting another point.
 */
function selectPoint(p: MapPoint, fly: boolean): void {
  const detailEl = document.getElementById('map-detail');
  if (!detailEl) return;
  activePointKey = pointKey(p);

  const cells = (p.details ?? [])
    .map((d) => `
      <div class="map-detail__cell">
        <div class="map-detail__cell-label">${escapeHtml(t(d.labelKey))}</div>
        <div class="map-detail__cell-value">${escapeHtml(d.value)}</div>
      </div>`)
    .join('');
  const statusLabel = t(`map.status.${p.status}`);
  const closeLabel = t('map.detail.close');
  // While the booking modal is in "picking another station" mode, the
  // detail card's primary CTA flips from "Reserveer" to "+ Toevoegen aan
  // reservering" - clicking it adds this station to the modal's pick list
  // and reopens the modal with the previous form values preserved.
  const inPickingMode = isPickingMore();
  const bookLabel = inPickingMode ? t('map.detail.btn_add_to_request') : t('map.detail.btn_request');
  const routeLabel = t('map.detail.btn_route');
  // Admin-only shortcut to the point's usage history. Rendered only when the
  // browser holds a sign-in session, so ordinary visitors never see it; the
  // admin page itself enforces auth and organisation scope.
  const usageUrl = hasStoredSession() ? usageHref(p.kind, p.id) : null;
  const usageLink = usageUrl
    ? `<a class="map-detail__admin-link" href="${escapeHtml(usageUrl)}" target="_blank" rel="noopener">${escapeHtml(t('map.detail.btn_usage'))} →</a>`
    : '';
  const addrLine = [p.address, p.city].filter(Boolean).join(', ');
  const gallery = (p.images && p.images.length > 0)
    ? `<div class="map-detail__gallery">${p.images.map((url) => `
        <a class="map-detail__gallery-item" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">
          <img src="${escapeHtml(url)}" alt="" loading="lazy"/>
        </a>`).join('')}</div>`
    : '';

  detailEl.innerHTML = `
    <button class="map-detail__close" type="button" aria-label="${escapeHtml(closeLabel)}">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    </button>
    <div class="map-detail__name">${escapeHtml(p.name)}</div>
    ${addrLine ? `<div class="map-detail__addr">${escapeHtml(addrLine)}</div>` : ''}
    <div style="margin-top:12px"><span class="status-tag status-tag--${escapeHtml(p.status)}">${escapeHtml(statusLabel)}</span></div>
    ${gallery}
    ${cells ? `<div class="map-detail__grid">${cells}</div>` : ''}
    <div class="map-detail__actions">
      <button class="btn btn--primary" type="button" id="detail-book">${escapeHtml(bookLabel)}</button>
      <button class="btn btn--ghost" type="button" id="detail-route">${escapeHtml(routeLabel)}</button>
    </div>
    ${usageLink}`;

  detailEl.classList.add('is-open');
  detailEl.querySelector<HTMLButtonElement>('.map-detail__close')?.addEventListener('click', () => {
    closeDetail();
  });
  detailEl.querySelector<HTMLButtonElement>('#detail-book')?.addEventListener('click', () => {
    const payload = { kind: p.kind, id: p.id, name: p.name, address: addrLine, connectorTypes: p.connectorTypes ?? [] };
    if (isPickingMore()) {
      addPickedPoint(payload);
      // 'added' / 'duplicate' - bookModal handles its own reopen/no-op.
      closeDetail();
    } else {
      openBookModal(payload);
    }
  });
  detailEl.querySelector<HTMLButtonElement>('#detail-route')?.addEventListener('click', () => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`, '_blank');
  });

  if (fly) map.flyTo([p.lat, p.lng], Math.max(map.getZoom(), 17), { duration: 0.6 });
  renderResultsList();
}

function closeDetail(): void {
  const detailEl = document.getElementById('map-detail');
  detailEl?.classList.remove('is-open');
  activePointKey = null;
  renderResultsList();
}

function isVisible(p: MapPoint): boolean {
  if (!layerVisible[p.kind]) return false;
  if (!statusVisible[p.status]) return false;
  // Below the cluster threshold, AC/DC are represented as combined city
  // clusters - the individual points (markers + list rows) drop out.
  if (isClusterZoom() && (p.kind === 'AcChargingStation' || p.kind === 'DcChargingStation')) return false;
  return true;
}

function applyVisibility(): void {
  for (const p of points) {
    const m = markersById[pointKey(p)];
    if (!m) continue;
    if (isVisible(p)) pointClusterGroup.addLayer(m);
    else pointClusterGroup.removeLayer(m);
  }
  // City charger clusters only at zoom <= threshold; above it the individual
  // by-bounds stations take over.
  if (isClusterZoom()) {
    if (!map.hasLayer(chargerClusterGroup)) chargerClusterGroup.addTo(map);
  } else if (map.hasLayer(chargerClusterGroup)) {
    map.removeLayer(chargerClusterGroup);
  }
  // Hint: while chargers are shown as city clusters (zoom <= threshold), tell
  // the user to zoom in to a street to see individual charging stations.
  const zoomHint = document.getElementById('charger-zoom-hint');
  if (zoomHint) {
    const chargersActive = envConfig.brand.features.publicMapChargingStations
      && (layerVisible.AcChargingStation || layerVisible.DcChargingStation);
    zoomHint.hidden = !(chargersActive && isClusterZoom());
  }
  renderResultsList();
}

/**
 * Aggregates AC + DC cluster rows into one combined marker per city, respecting
 * the active type-chip filter (so toggling DC off recomputes the totals shown).
 * Idempotent - wipes and re-creates the existing combined markers.
 */
function rebuildClusterMarkers(): void {
  const byRegion = new Map<string, ClusterRow>();
  const accumulate = (rows: ClusterRow[]) => {
    for (const r of rows) {
      const existing = byRegion.get(r.region);
      if (existing) existing.count += r.count;
      else byRegion.set(r.region, { region: r.region, count: r.count, lat: r.lat, lng: r.lng });
    }
  };
  if (layerVisible.AcChargingStation) accumulate(rawClusters.ac);
  if (layerVisible.DcChargingStation) accumulate(rawClusters.dc);

  chargerClusterGroup.clearLayers();
  for (const c of byRegion.values()) {
    if (c.count <= 0) continue;
    const marker = L.marker([c.lat, c.lng], { icon: defaultClusterIcon(c.count) });
    (marker.options as ChargerClusterMarkerOptions).stationCount = c.count;
    // One click on a single city dives past the cluster threshold so the
    // individual stations (from /by-bounds) load in.
    marker.on('click', () => map.flyTo([c.lat, c.lng], CLUSTER_ZOOM_MAX + 1));
    chargerClusterGroup.addLayer(marker);
  }
  applyVisibility();
}

function renderResultsList(): void {
  const filtered = points.filter(isVisible);
  // Stable sort by kind so the list always reads PS → WP → AC → DC,
  // regardless of which fetch completed first.
  filtered.sort((a, b) => RESULT_KIND_ORDER[a.kind] - RESULT_KIND_ORDER[b.kind]);
  const countEl = document.getElementById('result-count');
  if (countEl) countEl.textContent = String(filtered.length);
  const list = document.getElementById('map-results');
  if (!list) return;
  if (filtered.length === 0) {
    list.innerHTML = `<div style="padding:24px 22px;color:var(--sk-muted);font-size:14px;">${escapeHtml(t('map.no_matches'))}</div>`;
    return;
  }
  list.innerHTML = filtered.slice(0, 200).map((p) => {
    const meta = [p.city, p.typeLabel].filter((s) => s && s.length > 0).join(' · ');
    const specsHtml = (p.details ?? [])
      .map((d) => `<span class="spec-pill">${escapeHtml(d.value)}</span>`)
      .join('');
    const statusLabel = t(`map.status.${p.status}`);
    const activeClass = activePointKey === pointKey(p) ? ' is-active' : '';
    return `
      <div class="map-result${activeClass}" data-point-id="${escapeHtml(pointKey(p))}">
        <div class="map-result__head">
          <div>
            <div class="map-result__name">${escapeHtml(p.name)}</div>
            ${meta ? `<div class="map-result__meta">${escapeHtml(meta)}</div>` : ''}
          </div>
          <span class="status-tag status-tag--${escapeHtml(p.status)}">${escapeHtml(statusLabel)}</span>
        </div>
        ${specsHtml ? `<div class="map-result__specs">${specsHtml}</div>` : ''}
      </div>`;
  }).join('');
  list.querySelectorAll<HTMLElement>('.map-result').forEach((el) => {
    el.addEventListener('click', () => {
      const key = el.dataset.pointId ?? '';
      const point = points.find((p) => pointKey(p) === key);
      if (!point) return;
      selectPoint(point, true);
    });
  });
}

// ---- data fetching ---------------------------------------------------------

async function fetchPowerStations(): Promise<void> {
  try {
    const client = await api();
    const { data } = await client.get<Array<{
      id: number; powerStationId: string | null; latitude: number | null; longitude: number | null;
      organizationName: string; operator: string; city: string; street: string; postalCode: string;
      primaryFunction: string; capacityValue: string; connectorType: string;
      tariff: string; status: string; images: string[];
    }>>('/api/map/power-stations');
    for (const ps of data) {
      if (ps.latitude == null || ps.longitude == null) continue;
      const connectors = ps.connectorType.split(',').map((s) => s.trim()).filter(Boolean).join(', ');
      const p: MapPoint = {
        kind: 'PowerStation',
        id: ps.id,
        name: ps.organizationName || ps.powerStationId || `Power station ${ps.id}`,
        address: [ps.street, ps.postalCode].filter(Boolean).join(' '),
        city: ps.city,
        lat: ps.latitude,
        lng: ps.longitude,
        status: pointStatus(ps.status),
        tariff: ps.tariff,
        operator: ps.operator,
        typeLabel: ps.primaryFunction && ps.primaryFunction !== 'Unknown' ? ps.primaryFunction : t('map.filter_power_station'),
        images: ps.images ?? [],
        connectorTypes: ps.connectorType.split(',').map((s) => s.trim()).filter(Boolean),
        details: [
          ps.capacityValue && ps.capacityValue !== 'Unknown'
            ? { labelKey: 'map.detail.capacity', value: ps.capacityValue } : null,
          connectors
            ? { labelKey: 'map.detail.connection', value: connectors } : null,
          ps.primaryFunction && ps.primaryFunction !== 'Unknown'
            ? { labelKey: 'map.detail.type', value: ps.primaryFunction } : null,
          ps.tariff && ps.tariff !== 'Unknown'
            ? { labelKey: 'map.detail.tariff', value: ps.tariff } : null,
        ].filter((d): d is { labelKey: string; value: string } => d !== null),
      };
      points.push(p);
      refreshMarker(p);
    }
  } catch (err) {
    console.error('[map] failed to load power stations', err);
  }
}

async function fetchIndirectStations(): Promise<void> {
  try {
    const client = await api();
    const { data } = await client.get<Array<{
      id: number; indirectStationId: string | null; latitude: number | null; longitude: number | null;
      organizationName: string; operator: string; city: string; street: string; postalCode: string;
      primaryFunction: string; capacityValue: string; connectorType: string;
      tariff: string; status: string; images: string[];
    }>>('/api/map/indirect-stations');
    for (const ps of data) {
      if (ps.latitude == null || ps.longitude == null) continue;
      const connectors = ps.connectorType.split(',').map((s) => s.trim()).filter(Boolean).join(', ');
      const p: MapPoint = {
        kind: 'IndirectStation',
        id: ps.id,
        name: ps.organizationName || ps.indirectStationId || `Indirect station ${ps.id}`,
        address: [ps.street, ps.postalCode].filter(Boolean).join(' '),
        city: ps.city,
        lat: ps.latitude,
        lng: ps.longitude,
        status: pointStatus(ps.status),
        tariff: ps.tariff,
        operator: ps.operator,
        typeLabel: ps.primaryFunction && ps.primaryFunction !== 'Unknown' ? ps.primaryFunction : t('map.filter_indirect_station'),
        images: ps.images ?? [],
        connectorTypes: ps.connectorType.split(',').map((s) => s.trim()).filter(Boolean),
        details: [
          ps.capacityValue && ps.capacityValue !== 'Unknown'
            ? { labelKey: 'map.detail.capacity', value: ps.capacityValue } : null,
          connectors
            ? { labelKey: 'map.detail.connection', value: connectors } : null,
          ps.primaryFunction && ps.primaryFunction !== 'Unknown'
            ? { labelKey: 'map.detail.type', value: ps.primaryFunction } : null,
          ps.tariff && ps.tariff !== 'Unknown'
            ? { labelKey: 'map.detail.tariff', value: ps.tariff } : null,
        ].filter((d): d is { labelKey: string; value: string } => d !== null),
      };
      points.push(p);
      refreshMarker(p);
    }
  } catch (err) {
    console.error('[map] failed to load indirect stations', err);
  }
}

async function fetchWaterPoints(): Promise<void> {
  try {
    const client = await api();
    const { data } = await client.get<Array<{
      id: number; type: string | null; streetName: string | null; number: number | null;
      latitude: number | null; longitude: number | null; status: string;
    }>>('/api/map/water-points');
    for (const w of data) {
      if (w.latitude == null || w.longitude == null) continue;
      const p: MapPoint = {
        kind: 'WaterPoint',
        id: w.id,
        name: w.type ?? t('map.filter_water'),
        address: [w.streetName, w.number].filter(Boolean).join(' '),
        lat: w.latitude,
        lng: w.longitude,
        status: pointStatus(w.status),
        typeLabel: t('map.filter_water'),
        details: w.type
          ? [{ labelKey: 'map.detail.type', value: w.type }]
          : [],
      };
      points.push(p);
      refreshMarker(p);
    }
  } catch (err) {
    console.error('[map] failed to load water points', err);
  }
}

async function fetchClusters(prefix: 'ac' | 'dc'): Promise<void> {
  try {
    const client = await api();
    const { data } = await client.get<Array<{
      region: string; count: number; latitude: number; longitude: number;
    }>>(`/api/map/${prefix}-charging-stations/clusters`);
    rawClusters[prefix] = data.map((d) => ({
      region: d.region, count: d.count, lat: d.latitude, lng: d.longitude,
    }));
  } catch (err) {
    console.error(`[map] failed to load ${prefix} clusters`, err);
  }
}

interface ApiConnectorSummary {
  standard: string | null;
  powerType: string | null;
  maxPowerKw: number | null;
}

async function fetchChargingStationsByBounds(prefix: 'ac' | 'dc'): Promise<void> {
  try {
    const client = await api();
    const bounds = map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const { data } = await client.get<Array<{
      station: { id: number; name: string | null; operator: string | null;
                 city: string | null; address: string | null; postalCode: string | null;
                 longitude: number; latitude: number; status: string;
                 evseCount: number; connectors: ApiConnectorSummary[]; };
    }>>(`/api/map/${prefix}-charging-stations/by-bounds`, {
      params: { swLat: sw.lat, swLng: sw.lng, neLat: ne.lat, neLng: ne.lng },
    });
    const kind = prefix === 'ac' ? 'AcChargingStation' : 'DcChargingStation';
    const kindLabel = prefix === 'ac' ? t('map.filter_ac') : t('map.filter_dc');
    for (const item of data) {
      const s = item.station;
      const p: MapPoint = {
        kind,
        id: s.id,
        name: s.name ?? s.operator ?? `${prefix.toUpperCase()} charger`,
        address: [s.address, s.postalCode].filter(Boolean).join(' '),
        city: s.city,
        lat: s.latitude,
        lng: s.longitude,
        status: pointStatus(s.status),
        operator: s.operator,
        typeLabel: kindLabel,
        connectorTypes: Array.from(new Set(
          s.connectors.map((c) => c.standard ? prettyConnectorStandard(c.standard) : '').filter(Boolean))),
        details: buildChargerDetails(s.operator, s.evseCount, s.connectors),
      };
      // Replace any existing point at the same key (bbox refetches re-render).
      const existingIdx = points.findIndex((x) => pointKey(x) === pointKey(p));
      if (existingIdx >= 0) points[existingIdx] = p; else points.push(p);
      refreshMarker(p);
    }
    // A charging-station search hit outside the prior viewport wasn't in
    // `points`, so the search flew here and parked the hit. Now that this
    // viewport's chargers have loaded, open its detail if it turned up.
    if (pendingSearchHit) {
      const hit = pendingSearchHit;
      const match = points.find((p) =>
        Math.abs(p.lat - hit.lat) < 1e-5 && Math.abs(p.lng - hit.lng) < 1e-5);
      if (match) { pendingSearchHit = null; selectPoint(match, false); }
    }
  } catch (err) {
    console.error(`[map] failed to load ${prefix} charging stations`, err);
  }
}

/**
 * Detail-grid cells for an AC/DC charging station. Pulls in operator name,
 * EVSE count (parking bays), and one row per distinct connector with its
 * Standard / PowerType / max kW. Skips fields that came back missing rather
 * than rendering "Unknown".
 */
function buildChargerDetails(
  operator: string | null,
  evseCount: number,
  connectors: ApiConnectorSummary[],
): Array<{ labelKey: string; value: string }> {
  const out: Array<{ labelKey: string; value: string }> = [];
  if (operator) out.push({ labelKey: 'map.detail.operator', value: operator });
  if (evseCount > 0) out.push({ labelKey: 'map.detail.charging_points', value: String(evseCount) });
  for (const c of connectors) {
    const parts: string[] = [];
    if (c.standard) parts.push(prettyConnectorStandard(c.standard));
    if (c.powerType) parts.push(prettyPowerType(c.powerType));
    if (c.maxPowerKw != null) parts.push(`${c.maxPowerKw} kW`);
    if (parts.length > 0) out.push({ labelKey: 'map.detail.connector', value: parts.join(' · ') });
  }
  return out;
}

/** Strip the OCPI <c>IEC_62196_</c> prefix off Type-2 / CCS standards so the
 *  detail grid reads "T2" / "T2 COMBO" rather than the full identifier. */
function prettyConnectorStandard(raw: string): string {
  return raw
    .replace(/^IEC_62196_/, '')
    .replace(/_/g, ' ');
}

/** "AC_3_PHASE" → "AC 3-phase", "DC" → "DC". */
function prettyPowerType(raw: string): string {
  if (raw === 'DC') return 'DC';
  if (raw === 'AC_1_PHASE') return 'AC 1-phase';
  if (raw === 'AC_3_PHASE') return 'AC 3-phase';
  return raw.replace(/_/g, ' ');
}

// ---- wire up filters + search ----------------------------------------------

document.querySelectorAll<HTMLButtonElement>('.filter-chip[data-filter-type]').forEach((chip) => {
  chip.addEventListener('click', () => {
    const type = chip.dataset.filterType ?? '';
    if (type === '') {
      // "All" toggles every layer on.
      Object.keys(layerVisible).forEach((k) => { layerVisible[k] = true; });
      document.querySelectorAll<HTMLButtonElement>('.filter-chip[data-filter-type]').forEach((c) => c.classList.add('is-active'));
    } else {
      layerVisible[type] = !layerVisible[type];
      chip.classList.toggle('is-active', layerVisible[type]);
    }
    // AC/DC chip changes mean the cluster totals change too; rebuild covers
    // both the cluster markers and the per-point visibility in one pass.
    if (type === '' || type === 'AcChargingStation' || type === 'DcChargingStation') {
      rebuildClusterMarkers();
    } else {
      applyVisibility();
    }
  });
});

document.querySelectorAll<HTMLButtonElement>('.filter-chip[data-filter-status]').forEach((chip) => {
  chip.addEventListener('click', () => {
    const status = chip.dataset.filterStatus ?? '';
    statusVisible[status] = !statusVisible[status];
    chip.classList.toggle('is-active', statusVisible[status]);
    applyVisibility();
  });
});

// ---- hybrid search ----------------------------------------------------------
// Two-source dropdown: /api/map/search for known points (PowerStation,
// ChargingStation, WaterPoint) and Nominatim for free-text addresses scoped
// to the brand's country list. Click a point hit → flyTo + open detail when
// a marker is in scope (PowerStation/WaterPoint always; ChargingStation only
// after by-bounds has loaded for the current viewport). Click an address →
// flyTo only.

interface PointHit { label: string; lat: number; lng: number; typecat: string; }
interface AddressHit { label: string; lat: number; lng: number; }

const searchInput = document.getElementById('map-search-input') as HTMLInputElement | null;
const searchResultsEl = document.getElementById('map-search-results');
let searchDebounce: number | undefined;
// A point hit the search flew to but couldn't open yet (charging station not
// loaded for the prior viewport). Resolved by the next by-bounds load.
let pendingSearchHit: PointHit | null = null;
// The single highlight pin dropped when the user jumps to a searched address.
// Only one exists at a time - a new address jump replaces it, and selecting a
// real point clears it. Added straight to the map (not the cluster group) so
// it stays a distinct standalone marker.
let searchMarker: L.Marker | null = null;

searchInput?.addEventListener('input', () => {
  const q = searchInput.value.trim();
  pendingSearchHit = null;
  window.clearTimeout(searchDebounce);
  if (q.length < 3) {
    closeSearchDropdown();
    return;
  }
  searchDebounce = window.setTimeout(() => void runSearch(q), 250);
});

// Dismiss the dropdown when focus leaves the search area. setTimeout so a
// click on a row registers before the dropdown unmounts.
searchInput?.addEventListener('blur', () => {
  setTimeout(() => closeSearchDropdown(), 150);
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeSearchDropdown();
});

async function runSearch(q: string): Promise<void> {
  const [pointHits, addressHits] = await Promise.all([
    fetchPointHits(q),
    fetchAddressHits(q),
  ]);
  renderSearchDropdown(pointHits, addressHits);
}

async function fetchPointHits(q: string): Promise<PointHit[]> {
  try {
    const client = await api();
    // The API returns Latitude/Longitude (camelCased to latitude/longitude);
    // map them onto PointHit's lat/lng so flyTo + the marker match work.
    const { data } = await client.get<Array<{ label: string; latitude: number; longitude: number; typecat: string }>>(
      '/api/map/search', { params: { q } });
    return data.slice(0, 5).map((d) => ({ label: d.label, lat: d.latitude, lng: d.longitude, typecat: d.typecat }));
  } catch {
    return [];
  }
}

async function fetchAddressHits(q: string): Promise<AddressHit[]> {
  const country = envConfig.brand.searchCountryCodes;
  const params = new URLSearchParams({ format: 'json', limit: '5', q });
  if (country) params.set('countrycodes', country);
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
      headers: { 'Accept-Language': getLanguage() || envConfig.brand.defaultLanguage },
    });
    if (!response.ok) return [];
    const data = await response.json() as Array<{ display_name: string; lat: string; lon: string }>;
    return data.map((d) => ({ label: d.display_name, lat: Number(d.lat), lng: Number(d.lon) }));
  } catch {
    return [];
  }
}

// A classic teardrop location pin - deliberately unlike the small round type
// markers so a searched address reads as a distinct highlight. Inline-styled
// so it needs no designer CSS. Anchored at the tip (bottom-centre).
function searchPinIcon(): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="color:var(--sk-navy-800,#0a2a5c);line-height:0;filter:drop-shadow(0 2px 4px rgba(10,42,92,0.4));">
      <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor" stroke="#fff" stroke-width="1.2">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z"/>
      </svg></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 32],
  });
}

// Drop (or move) the highlight pin at a searched address and open a popup with
// its label. Sits above every other marker so it's never hidden behind a
// cluster or type marker.
function placeSearchMarker(lat: number, lng: number, label: string): void {
  clearSearchMarker();
  searchMarker = L.marker([lat, lng], { icon: searchPinIcon(), zIndexOffset: 3000, keyboard: false })
    .addTo(map)
    .bindPopup(escapeHtml(label))
    .openPopup();
}

function clearSearchMarker(): void {
  if (searchMarker) {
    map.removeLayer(searchMarker);
    searchMarker = null;
  }
}

function renderSearchDropdown(pointHits: PointHit[], addressHits: AddressHit[]): void {
  if (!searchResultsEl) return;
  if (pointHits.length === 0 && addressHits.length === 0) {
    searchResultsEl.innerHTML = `<div class="map-search__empty">${escapeHtml(t('map.no_matches'))}</div>`;
    searchResultsEl.hidden = false;
    return;
  }
  const sections: string[] = [];
  if (pointHits.length > 0) {
    sections.push(`<div class="map-search__group">${escapeHtml(t('map.search.points'))}</div>`);
    sections.push(pointHits.map((h, i) => `
      <button type="button" class="map-search__row" data-kind="point" data-idx="${i}">
        <div>${escapeHtml(h.label || '-')}</div>
        <div class="map-search__row-meta">${escapeHtml(typecatLabel(h.typecat))}</div>
      </button>`).join(''));
  }
  if (addressHits.length > 0) {
    sections.push(`<div class="map-search__group">${escapeHtml(t('map.search.addresses'))}</div>`);
    sections.push(addressHits.map((h, i) => `
      <button type="button" class="map-search__row" data-kind="address" data-idx="${i}">
        <div>📍 ${escapeHtml(h.label)}</div>
      </button>`).join(''));
  }
  searchResultsEl.innerHTML = sections.join('');
  searchResultsEl.hidden = false;

  searchResultsEl.querySelectorAll<HTMLButtonElement>('.map-search__row').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = Number(btn.dataset.idx);
      if (btn.dataset.kind === 'point') {
        const hit = pointHits[idx];
        if (hit) handlePointHit(hit);
      } else {
        const hit = addressHits[idx];
        if (hit) {
          map.flyTo([hit.lat, hit.lng], 17, { duration: 0.6 });
          placeSearchMarker(hit.lat, hit.lng, hit.label);
        }
      }
      closeSearchDropdown();
      if (searchInput) searchInput.value = '';
    });
  });
}

function closeSearchDropdown(): void {
  if (searchResultsEl) {
    searchResultsEl.hidden = true;
    searchResultsEl.innerHTML = '';
  }
}

function typecatLabel(typecat: string): string {
  if (typecat === 'powerstation') return t('map.filter_power_station');
  if (typecat === 'indirectstation') return t('map.filter_indirect_station');
  if (typecat === 'water-point') return t('map.filter_water');
  if (typecat === 'charging-station') return `${t('map.filter_ac')} / ${t('map.filter_dc')}`;
  return typecat;
}

/**
 * Tries to open the matching point's detail panel. Falls back to flyTo when
 * we don't have a marker for it (e.g. the search hit is a charging station
 * outside the current bounds).
 */
function handlePointHit(hit: PointHit): void {
  // Jumping to a known point supersedes any searched-address pin.
  clearSearchMarker();
  const match = points.find((p) =>
    Math.abs(p.lat - hit.lat) < 1e-5 && Math.abs(p.lng - hit.lng) < 1e-5);
  if (match) {
    pendingSearchHit = null;
    selectPoint(match, true);
  } else {
    // Not loaded yet (charging station outside the prior viewport). Fly in to
    // the same focus zoom as a marker select - never zoom out - and park the
    // hit so the next by-bounds load opens its detail.
    pendingSearchHit = hit;
    map.flyTo([hit.lat, hit.lng], Math.max(map.getZoom(), 17), { duration: 0.6 });
  }
}

// Re-render JS-built UI when the language switcher fires. The static HTML
// already gets retranslated by the i18n applyToRoot pass; this catches the
// dynamic surfaces (result list, open detail panel) that bake t() calls
// into innerHTML at render time.
document.addEventListener('sk-language-changed', () => {
  renderResultsList();
  if (activePointKey) {
    const point = points.find((p) => pointKey(p) === activePointKey);
    if (point) selectPoint(point, false);
  }
});

// ---- collapsible sidebar ----------------------------------------------------
// Toggle the sidebar column to 0 (full-width map) and back. Persisted so the
// choice survives reloads; Leaflet is told to recompute after the CSS grid
// transition so tiles fill the new width.
const sidebarToggle = document.getElementById('sidebar-toggle');
const mapShell = document.querySelector('.map-shell');
const SIDEBAR_COLLAPSED_KEY = 'sk-map-sidebar-collapsed';

if (sidebarToggle && mapShell) {
  try {
    if (localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1') {
      mapShell.classList.add('map-shell--sidebar-collapsed');
      setTimeout(() => map.invalidateSize(), 0);
    }
  } catch { /* localStorage unavailable - start expanded */ }

  sidebarToggle.addEventListener('click', () => {
    const collapsed = mapShell.classList.toggle('map-shell--sidebar-collapsed');
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
    // Let the 0.25s grid transition finish before Leaflet remeasures.
    setTimeout(() => map.invalidateSize(), 280);
  });
}

// ---- boot ------------------------------------------------------------------

let bboxDebounce: number | undefined;
// Per-customer feature gates - Bristol's public map intentionally omits
// charging stations + water points. Skip the fetch entirely so the API
// isn't hit, and hide the corresponding filter chips so the legend
// doesn't list categories you can't see on the map.
const showChargers = envConfig.brand.features.publicMapChargingStations;
const showWaterPoints = envConfig.brand.features.publicMapWaterPoints;
if (!showChargers) {
  document.querySelectorAll('.filter-chip[data-filter-type="AcChargingStation"], .filter-chip[data-filter-type="DcChargingStation"], .map-legend__row[data-legend-type="AcChargingStation"], .map-legend__row[data-legend-type="DcChargingStation"]')
    .forEach((el) => el.remove());
}
if (!showWaterPoints) {
  document.querySelectorAll('.filter-chip[data-filter-type="WaterPoint"], .map-legend__row[data-legend-type="WaterPoint"]')
    .forEach((el) => el.remove());
}

// At low zoom the by-bounds fetch is wasted (clusters cover that range and
// the backend rejects wide bounds anyway), so the moveend handler bails when
// in cluster mode.
map.on('moveend', () => {
  if (!showChargers) return;
  if (isClusterZoom()) return;
  window.clearTimeout(bboxDebounce);
  bboxDebounce = window.setTimeout(() => {
    void fetchChargingStationsByBounds('ac');
    void fetchChargingStationsByBounds('dc');
  }, 250);
});

// Crossing the cluster threshold flips which layer is visible. On zoom-in
// past the threshold we kick a by-bounds fetch immediately so users don't
// see a blank moment between clusters disappearing and points loading.
map.on('zoomend', () => {
  applyVisibility();
  if (!showChargers) return;
  if (!isClusterZoom()) {
    void fetchChargingStationsByBounds('ac').then(() => applyVisibility());
    void fetchChargingStationsByBounds('dc').then(() => applyVisibility());
  }
});

void fetchPowerStations().then(() => applyVisibility());
void fetchIndirectStations().then(() => applyVisibility());
if (showWaterPoints) void fetchWaterPoints().then(() => applyVisibility());
// Clusters: always fetch (cheap, server-cached for 30 min). Points are only
// fetched on boot if we're starting at a zoom that supports them.
if (showChargers) {
  void Promise.all([fetchClusters('ac'), fetchClusters('dc')]).then(() => rebuildClusterMarkers());
  if (!isClusterZoom()) {
    void fetchChargingStationsByBounds('ac').then(() => applyVisibility());
    void fetchChargingStationsByBounds('dc').then(() => applyVisibility());
  }
}

// Polygon area-request tool - self-bootstraps if its DOM controls exist.
import('./polygon');
